import type { ReactNode } from "react";

import { colorsPalettes } from "@similarweb/styles";
import { Badge, OnOffSwitch, OnOffSwitchSize } from "@similarweb/ui-components";
import { Flex, Text } from "@similarweb/ui-components/foundations";

type SettingsCardProps = {
    isSelected: boolean;
    onToggle: () => void;
    title: string;
    description?: string;
    icon: ReactNode;
    children?: ReactNode;
    dataAutomation?: string;
    isNew?: boolean;
};

export const SettingsCard = ({
    isNew = false,
    isSelected,
    onToggle,
    title,
    description,
    icon,
    children,
    dataAutomation,
}: SettingsCardProps) => {
    return (
        <Flex
            maxWidth="calc(50% - 4px)"
            flex="1 1 50%"
            flexDirection="column"
            gap="8px"
            padding="10px 12px"
            borderRadius="10px"
            border={`1px solid ${isSelected ? colorsPalettes.blue["400"] : colorsPalettes.midnight["50"]}`}
            background={isSelected ? colorsPalettes.blue["50"] : colorsPalettes.carbon["0"]}
            transition="border-color 0.2s ease-in-out, background-color 0.2s ease-in-out"
            data-automation={dataAutomation}
        >
            <Flex
                justifyContent="space-between"
                alignItems="flex-start"
                width="100%"
            >
                <Flex
                    alignItems="center"
                    gap="4px"
                >
                    <Flex
                        width="22px"
                        height="22px"
                        borderRadius="4px"
                        padding="3px"
                        alignItems="center"
                        justifyContent="center"
                        flexShrink={0}
                        background={isSelected ? colorsPalettes.blue["100"] : colorsPalettes.midnight["50"]}
                        transition="background-color 0.2s ease-in-out"
                    >
                        {icon}
                    </Flex>
                    {isNew ? (
                        <Flex data-automation={dataAutomation ? `${dataAutomation}-new-badge` : undefined}>
                            <Badge type="new" />
                        </Flex>
                    ) : null}
                </Flex>
                <Flex data-automation={dataAutomation ? `${dataAutomation}-toggle` : undefined}>
                    <OnOffSwitch
                        size={OnOffSwitchSize.medium}
                        isSelected={isSelected}
                        onClick={onToggle}
                    />
                </Flex>
            </Flex>
            <Flex
                flexDirection="column"
                gap="10px"
            >
                <Text
                    as="span"
                    fontVariant="primaryItem"
                    color={colorsPalettes.carbon["500"]}
                    minHeight={description ? "32px" : undefined}
                >
                    {title}
                </Text>
                {description ? (
                    <Text
                        as="p"
                        fontVariant="sectionTitle"
                        color={colorsPalettes.carbon["250"]}
                        lineHeight="14px"
                        margin="0"
                    >
                        {description}
                    </Text>
                ) : null}
            </Flex>
            {children}
        </Flex>
    );
};
